import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5sjmbm6ql8jq9ml8t9g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Ready to secure your property? Contact us today for a free consultation and discover how our <strong>security services</strong>, <strong>storm preparedness</strong>, and <strong>preventative maintenance</strong> solutions can protect your investment.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-10 text-white h-full">
              <h3 className="text-3xl font-bold mb-6">Why Choose SafePoint Solutions?</h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">20+ Years Experience</h4>
                    <p className="text-teal-100 text-sm leading-relaxed">
                      Backed by extensive state and federal law enforcement expertise
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-24-hours-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">24/7 Availability</h4>
                    <p className="text-teal-100 text-sm leading-relaxed">
                      Round-the-clock support and emergency response services
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Trusted Professionals</h4>
                    <p className="text-teal-100 text-sm leading-relaxed">
                      Certified experts dedicated to protecting what matters most
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Comprehensive Solutions</h4>
                    <p className="text-teal-100 text-sm leading-relaxed">
                      All-in-one security, storm prep, and maintenance services
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-10 border-t border-white/20">
                <h4 className="text-lg font-semibold mb-4">Contact Information</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <i className="ri-phone-line text-xl"></i>
                    <span className="text-sm">786-447-6283</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-mail-line text-xl"></i>
                    <span className="text-sm">Info@safepoint-solutions.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-time-line text-xl"></i>
                    <span className="text-sm">Available 24/7 for Emergencies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm"
                  placeholder="786-447-6283"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Interested In *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Select a service</option>
                  <option value="Security Services">Security Services</option>
                  <option value="Storm Preparedness">Storm Preparedness</option>
                  <option value="Preventative Maintenance">Preventative Maintenance</option>
                  <option value="Risk Assessment">Risk Assessment</option>
                  <option value="Surveillance Systems">Surveillance Systems</option>
                  <option value="Emergency Response">Emergency Response</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-600 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your security needs..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll get back to you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white text-base font-semibold rounded-lg hover:bg-teal-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}